// /apps/patient/src/components/navigation/MobileTopBar.tsx

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, X, Calendar, FileText, Heart, ClipboardList, LogOut, CreditCard } from 'lucide-react';
import { usePatient } from '../../contexts/PatientContext';
import { useFeatures } from '../../contexts/FeatureContext';
import { cn } from '../../lib/utils';

export function MobileTopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patient, logout } = usePatient();
  const { isStandalone, hasFeature } = useFeatures();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // Close menu on navigation
  useEffect(() => {
    setShowMenu(false);
  }, [location]);

  const showBackButton = location.pathname !== '/dashboard';

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-40 lg:hidden">
        <div
          className="flex items-center justify-between h-14 px-4"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          {/* Left: Back button or spacer */}
          <div className="flex-1 flex items-center">
            {showBackButton ? (
              <button
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
            ) : (
              <div className="w-9" /> // Spacer to keep title centered
            )}
          </div>

          {/* Center: Brand */}
          <div className="flex items-center">
            {isStandalone ? (
              <h1 className="text-lg font-bold text-primary">Ready2Exercise</h1>
            ) : (
              <div className="flex items-center space-x-2">
                <img
                  src="/prism-logo.png"
                  alt="Prism"
                  className="h-6 w-6"
                />
                <h1 className="text-lg font-bold text-primary">PrismEHR</h1>
              </div>
            )}
          </div>

          {/* Right: User menu */}
          <div className="flex-1 flex items-center justify-end">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="relative h-9 w-9 min-h-[36px] min-w-[36px] rounded-full bg-primary/10 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary flex-shrink-0 aspect-square"
              aria-label="User menu"
              aria-expanded={showMenu}
            >
              <span className="text-primary font-semibold text-sm leading-none">
                {patient?.first_name?.[0]?.toUpperCase() || 'P'}
              </span>
            </button>
          </div>
        </div>

        {/* Dropdown Menu */}
        {showMenu && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setShowMenu(false)}
            />

            {/* Menu */}
            <div
              ref={menuRef}
              className="absolute top-full right-4 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
              style={{ top: `calc(100% + env(safe-area-inset-top))` }}
            >
              {/* User info */}
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">
                  {patient?.first_name} {patient?.last_name}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isStandalone ? 'Free Plan' : 'Patient'}
                </p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    navigate('/profile');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                >
                  Profile
                </button>

                <button
                  onClick={() => {
                    setShowMenu(false);
                    navigate('/calendar');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Calendar
                </button>

                {hasFeature('clinical_records') && (
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      navigate('/records');
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Health Records
                  </button>
                )}

                {hasFeature('prescriptions') && (
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      navigate('/prescriptions');
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center"
                  >
                    <Heart className="h-4 w-4 mr-2" />
                    Prescriptions
                  </button>
                )}

                {hasFeature('forms') && (
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      navigate('/forms');
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center"
                  >
                    <ClipboardList className="h-4 w-4 mr-2" />
                    Forms
                  </button>
                )}

                <button
                  onClick={() => {
                    setShowMenu(false);
                    navigate('/billing');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Billing
                </button>

                <div className="border-t border-gray-100 my-1" />

                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}